/**
 * Auth Store Factory
 *
 * Creates a reusable auth store instance for any client app.
 * Uses localStorage for persistent guest identity (survives tab close).
 *
 * SECURITY: Tokens are stored in httpOnly cookies by the server.
 * This store only tracks user info for UI purposes.
 */

import { useSyncExternalStore } from "react";
import * as authApi from "./auth-api";
import { USER_STORAGE_KEY } from "./constants";
import {
  formatGuestLabel,
  getOrCreateGuestId,
  isGuestEmail,
  saveGuestId,
  saveGuestNickname,
} from "./guest-identity";
import type {
  ApiResponse,
  AuthState,
  AuthStoreConfig,
  LoginResponseData,
  User,
} from "./types";

/** Map API user response to User */
function mapApiUser(apiUser: {
  userId: string;
  email: string;
  isGuest?: boolean;
  guestDisplayName?: string;
}): User {
  const guestName = apiUser.guestDisplayName?.trim();
  const isGuest = apiUser.isGuest ?? isGuestEmail(apiUser.email);
  return {
    id: apiUser.userId,
    displayName:
      isGuest && guestName && guestName.length > 0 ? guestName : apiUser.email,
    email: apiUser.email,
  };
}

function extractErrorMessage<T>(
  response: ApiResponse<T>,
  fallbackMessage: string,
): string {
  if (response.details && response.details.length > 0) {
    return response.details.map((d) => d.message).join("\n");
  }
  return response.error ?? response.errorCode ?? fallbackMessage;
}

/** Runtime validation for User objects from storage */
function isValidUser(value: unknown): value is User {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as User).id === "string" &&
    typeof (value as User).displayName === "string" &&
    typeof (value as User).email === "string"
  );
}

/** Load user info from localStorage */
function loadUserFromStorage(storageKey: string): User | null {
  try {
    const userJson = localStorage.getItem(storageKey);
    if (userJson) {
      const parsed: unknown = JSON.parse(userJson);
      if (isValidUser(parsed)) {
        return parsed;
      }
      localStorage.removeItem(storageKey);
    }
  } catch {
    // Ignore errors loading from storage
  }
  return null;
}

/** Save user info to localStorage */
function saveUserToStorage(storageKey: string, user: User | null): void {
  try {
    if (user) {
      localStorage.setItem(storageKey, JSON.stringify(user));
    } else {
      localStorage.removeItem(storageKey);
    }
  } catch {
    // localStorage may be unavailable
  }
}

/** Initial auth state */
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

/**
 * Create an auth store instance.
 *
 * Each app creates its own instance with its own config:
 * ```typescript
 * const { store, actions, hooks, utils } = createAuthStore({
 *   baseUrl: import.meta.env.VITE_AUTH_SERVICE_URL ?? "http://localhost:3001",
 * });
 * ```
 */
export function createAuthStore(config: AuthStoreConfig) {
  const {
    baseUrl,
    authPathPrefix = "/auth",
    storageKey = USER_STORAGE_KEY,
    guestStorageKey,
  } = config;

  // Start as loading so that route guards wait until initialize() completes.
  // Without this, guards fire with isAuthenticated=false before initialize()
  // has a chance to set isLoading=true, causing premature redirects to /login.
  let state: AuthState = { ...initialState, isLoading: true };
  const listeners = new Set<() => void>();

  function notify(): void {
    for (const listener of listeners) {
      listener();
    }
  }

  async function runCredentialAuth(
    request: () => Promise<ApiResponse<LoginResponseData>>,
    fallbackErrorMessage: string,
  ): Promise<boolean> {
    state = { ...state, isLoading: true, error: null };
    notify();

    try {
      const response = await request();

      if (response.success && response.data) {
        const user = mapApiUser(response.data.user);
        state = {
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        };
        saveUserToStorage(storageKey, user);
        notify();
        return true;
      }

      state = {
        ...state,
        isLoading: false,
        error: extractErrorMessage(response, fallbackErrorMessage),
      };
      notify();
      return false;
    } catch {
      state = {
        ...state,
        isLoading: false,
        error: "Network error. Please try again.",
      };
      notify();
      return false;
    }
  }

  const actions = {
    /** Initialize auth state - verify session with server */
    initialize: async (): Promise<void> => {
      const cachedUser = loadUserFromStorage(storageKey);

      if (cachedUser) {
        state = {
          ...state,
          user: cachedUser,
          isAuthenticated: true,
          isLoading: true,
        };
        notify();
      } else {
        state = { ...initialState, isLoading: true };
        notify();
      }

      try {
        let response = await authApi.verifySession(baseUrl, authPathPrefix);
        if (!response.success) {
          // The 1d access token may simply have expired while the 7d refresh
          // cookie is still valid — silently refresh once and re-check, so a
          // returning user is not treated as logged out.
          const refreshed = await authApi.refreshToken(baseUrl, authPathPrefix);
          if (refreshed.success) {
            response = await authApi.verifySession(baseUrl, authPathPrefix);
          }
        }
        if (response.success && response.data) {
          const user = mapApiUser(response.data.user);
          state = {
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          };
          saveUserToStorage(storageKey, user);
        } else {
          state = { ...initialState };
          saveUserToStorage(storageKey, null);
        }
      } catch {
        state = { ...state, isLoading: false };
      }
      notify();
    },

    /** Manually verify the current session via GET /auth/me */
    verifySession: async (): Promise<boolean> => {
      state = { ...state, isLoading: true, error: null };
      notify();

      try {
        const response = await authApi.verifySession(baseUrl, authPathPrefix);

        if (response.success && response.data) {
          const user = mapApiUser(response.data.user);
          state = {
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          };
          saveUserToStorage(storageKey, user);
          notify();
          return true;
        }

        state = {
          ...initialState,
          error: extractErrorMessage(response, "Session is not valid."),
        };
        saveUserToStorage(storageKey, null);
        notify();
        return false;
      } catch {
        state = {
          ...state,
          isLoading: false,
          error: "Network error. Please try again.",
        };
        notify();
        return false;
      }
    },

    /** Register with email/password via POST /auth/register */
    register: async (
      email: string,
      password: string,
      name?: string,
    ): Promise<boolean> => {
      return runCredentialAuth(
        () => authApi.register(baseUrl, email, password, name, authPathPrefix),
        "Registration failed",
      );
    },

    /** Login with email/password via POST /auth/login */
    login: async (email: string, password: string): Promise<boolean> => {
      return runCredentialAuth(
        () => authApi.login(baseUrl, email, password, authPathPrefix),
        "Login failed",
      );
    },

    /** Login as guest using persistent guestId via POST /auth/guest */
    loginAsGuest: async (guestDisplayName?: string): Promise<boolean> => {
      const guestId = getOrCreateGuestId(guestStorageKey);
      const success = await runCredentialAuth(
        () =>
          authApi.guestLogin(
            baseUrl,
            guestId,
            guestDisplayName,
            authPathPrefix,
          ),
        "Guest login failed",
      );
      if (success && guestDisplayName) {
        saveGuestNickname(guestId, guestDisplayName);
      }
      return success;
    },

    /**
     * Login as guest with a caller-provided guestId.
     * Used for deterministic share-link guest identity restoration.
     */
    loginAsGuestWithId: async (
      guestId: string,
      guestDisplayName?: string,
    ): Promise<boolean> => {
      saveGuestId(guestId, guestStorageKey);
      const success = await runCredentialAuth(
        () =>
          authApi.guestLogin(
            baseUrl,
            guestId,
            guestDisplayName,
            authPathPrefix,
          ),
        "Guest login failed",
      );
      if (success && guestDisplayName) {
        saveGuestNickname(guestId, guestDisplayName);
      }
      return success;
    },

    /** Login or register with Google OAuth ID token via POST /auth/google */
    loginWithGoogle: async (idToken: string): Promise<boolean> => {
      return runCredentialAuth(
        () => authApi.googleLogin(baseUrl, idToken, authPathPrefix),
        "Google login failed",
      );
    },

    /** Logout current user */
    logout: async (): Promise<void> => {
      try {
        await authApi.logout(baseUrl, authPathPrefix);
      } catch {
        // Continue with local cleanup even if server logout fails
      }
      state = { ...initialState };
      saveUserToStorage(storageKey, null);
      notify();
    },

    /** Clear error */
    clearError: (): void => {
      state = { ...state, error: null };
      notify();
    },
  };

  const store = {
    getState: (): AuthState => state,
    subscribe: (listener: () => void): (() => void) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };

  const hooks = {
    /** Hook to access auth state */
    useAuth: (): AuthState => {
      return useSyncExternalStore(store.subscribe, store.getState);
    },

    /** Hook to access auth actions */
    useAuthActions: (): typeof actions => {
      return actions;
    },

    /** Hook to check if authenticated */
    useIsAuthenticated: (): boolean => {
      const { isAuthenticated } = useSyncExternalStore(
        store.subscribe,
        store.getState,
      );
      return isAuthenticated;
    },
  };

  const utils = {
    /** Get display label for a user (handles guest formatting) */
    getUserLabel: (user: User | null): string => {
      if (!user) return "";
      if (isGuestEmail(user.email)) {
        // Prefer readable guestDisplayName saved into displayName by
        // mapApiUser; fall back to short label only if the email itself
        // is still stored as displayName (legacy cache).
        if (user.displayName && user.displayName !== user.email) {
          return user.displayName;
        }
        return formatGuestLabel(user.email);
      }
      return user.displayName;
    },

    /** Get current user ID (for API calls) */
    getCurrentUserId: (): string | null => {
      return state.user?.id ?? null;
    },
  };

  return { store, actions, hooks, utils };
}
